/**
 * OTD Settings Tab Component
 * Configures On-Time Delivery color bands and customer-list quick-filter ranges.
 */
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Clock, Info, TrendingUp, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { purchasingAPI } from '../../../lib/api';
import { PerformanceFormData } from './types';

interface OTDSettingsTabProps {
  formData: PerformanceFormData;
  onInputChange: (field: string, value: string | number | boolean) => void;
}

export function OTDSettingsTab({ formData, onInputChange }: OTDSettingsTabProps) {
  const excellent = parseFloat(formData.otd_excellent_threshold) || 90;
  const good = parseFloat(formData.otd_good_threshold) || 70;
  const poor = parseFloat(formData.otd_poor_threshold) || 50;

  const [recomputing, setRecomputing] = useState(false);
  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await purchasingAPI.recomputeOtd();
      toast.success(res.data?.message || 'OTD recompute started.');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to start OTD recompute';
      toast.error(msg);
    } finally {
      setRecomputing(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-primary" />
            <CardTitle>On-Time Delivery (OTD) Settings</CardTitle>
          </div>
          <CardDescription>
            Configure OTD percentage bands for customer card colours and quick filters on the Customers page.
            OTD is measured against the Supplier Promise Date.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* OTD Thresholds */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              OTD Range Thresholds
            </h3>
            <p className="text-sm text-muted-foreground">
              Set the percentage breakpoints for OTD colour coding and quick-filter chips.
              Thresholds must be ordered: Excellent &gt; Good &gt; Poor.
            </p>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="otd_excellent_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-green-500" />
                  Excellent (Green)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="otd_excellent_threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.otd_excellent_threshold}
                    onChange={(e) => onInputChange('otd_excellent_threshold', e.target.value)}
                  />
                  <span>%+</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otd_good_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-yellow-500" />
                  Good (Amber)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="otd_good_threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.otd_good_threshold}
                    onChange={(e) => onInputChange('otd_good_threshold', e.target.value)}
                  />
                  <span>%+</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="otd_poor_threshold" className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-red-500" />
                  Poor (Red)
                </Label>
                <div className="flex items-center gap-2">
                  <Input
                    id="otd_poor_threshold"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={formData.otd_poor_threshold}
                    onChange={(e) => onInputChange('otd_poor_threshold', e.target.value)}
                  />
                  <span>% or below</span>
                </div>
              </div>
            </div>
          </div>

          {/* Preview of filter bands */}
          <div className="space-y-3 border-t pt-4">
            <h3 className="font-medium text-sm">Quick-filter preview</h3>
            <p className="text-sm text-muted-foreground">
              These bands appear as quick-filter chips on the Customers list page.
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                Excellent ≥{excellent}%
              </Badge>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                Good {good}–{(excellent - 0.01).toFixed(0)}%
              </Badge>
              {poor < good && (
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                  Fair {poor}–{(good - 0.01).toFixed(0)}%
                </Badge>
              )}
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                Poor &lt;{good}%
              </Badge>
            </div>
          </div>

          {/* OTD Formula Info */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Info className="h-4 w-4" />
              <span className="font-medium">OTD Calculation</span>
            </div>
            <code className="block text-sm p-2 bg-background rounded border">
              OTD % = (On-time deliveries) / (Total deliveries with Supplier Promise Date) × 100
            </code>
            <p className="text-sm text-muted-foreground">
              A delivery counts as on-time when the supplier&apos;s delivery date (from the ASN) is on or before the Supplier Promise Date.
              Lines without a promise date are excluded from the calculation.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            <CardTitle>Recompute OTD</CardTitle>
          </div>
          <CardDescription>
            Each PO line stores its OTD verdict (early / on-time / late) as the single source of truth read by the
            dashboards and drill-downs. It refreshes automatically when a line is delivered or overridden, and nightly
            to catch lines that have become overdue. Use this to recompute every line on demand.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRecompute} disabled={recomputing}>
            {recomputing
              ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Starting…</>)
              : (<><RefreshCw className="h-4 w-4 mr-2" />Recompute OTD now</>)}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            Runs in the background; verdicts update shortly.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
